import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  ScanLine,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * TIER-1 post q13. Flagship Shape-A query: "organize scanned documents by
 * content". Claude already cites getsortio.com here. Lead with the core
 * insight: a scanned file has no useful name and no metadata, so you have to
 * read the content. OCR when there is no text layer, then file by what the
 * document actually is. Covers mixed-scan drawers, paperless workflows, the
 * OCR-robustness advantage over regex tools, and a per-type folder structure.
 * Comparison: paperless-ngx / Hazel / dedicated OCR tools vs Sortio.
 */

const PUBLISHED = '2026-06-05';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/organize-scanned-documents-by-content';

const FAQ = [
  {
    question: 'How do you organize scanned documents by content?',
    answer:
      'You read the content, because a scanned file has no useful name and no metadata to sort on. The workflow has two steps. First, make sure each scan has a text layer: scans saved as searchable PDFs already do, and image-only scans get one in bulk from a free tool like OCRmyPDF. Second, Sortio reads that text with an LLM and files each document by what it actually is (a lease, a 1099, an invoice, a signed contract) instead of by a filename like scan_0421.pdf. Point it at the folder, run Preview to confirm the proposed names and destinations, then apply, and a drawer of scanned paper becomes a searchable, correctly filed archive in one pass.',
  },
  {
    question: 'What if my scanned PDFs have no text layer at all?',
    answer:
      'That is the common case for documents scanned to image-only PDF or saved straight from a phone camera. Sortio does not run its own OCR, so those files need a text layer added first. OCRmyPDF is a solid free option that adds a searchable text layer to existing PDFs in bulk (one command over the whole folder), and Sortio reads the layer it produces. Once the layer exists, the document gets classified by what is inside it rather than by its filename. Files with no text layer still sort, but only on filename, dates, and folder context.',
  },
  {
    question: 'How is Sortio different from paperless-ngx?',
    answer:
      'paperless-ngx is an excellent self-hosted document management system. It OCRs incoming scans, stores them in a database, and lets you tag and full-text search them. The classification combines rule and pattern matching with a machine-learning "Auto" mode that learns correspondents, document types, and tags from the documents you have already tagged, and it keeps documents inside its own archive rather than your normal folder tree. Sortio is lighter. It reads each scan with an LLM, decides what the document is from the meaning of the page, and files it into ordinary folders on your Mac or PC with a clean name. If you want a full DMS with a web UI and a stored archive, paperless-ngx is the right tool. If you want your scans named and filed into plain folders by content, Sortio is the simpler fit.',
  },
  {
    question: 'Can Hazel sort scanned documents by their content?',
    answer:
      'Hazel can match a regex against a PDF text layer and act on the result, so for clean scans from a single consistent source it can route files. It breaks down on a mixed drawer of scans where OCR text drifts between documents and every source has a different layout, because each pattern needs its own rule and the regex stops matching when the text comes out slightly different. Sortio reads the scan semantically, so OCR noise that breaks a regex does not break the classification.',
  },
  {
    question: 'What folder structure works best for scanned documents?',
    answer:
      'File by document type at the top level, then by year, with the person or entity in the filename. A structure like Documents/Scans/Tax/2026/, Documents/Scans/Medical/2026/, Documents/Scans/Legal/2026/, Documents/Scans/Property/2026/ keeps related paper together and makes a tax-time or audit pull a single-folder operation. Name each file {YYYY-MM-DD}_{DocType}_{Party}.pdf so it sorts chronologically and tells you what it is at a glance.',
  },
  {
    question: 'Is it safe to run AI on sensitive scanned documents?',
    answer:
      'It can be, depending on the mode you choose. Sortio supports local-only inference through Ollama (Llama 3, Mistral), so the content of medical, legal, and financial scans never leaves your machine. Managed AI or bring-your-own-key are also available when you want more speed and accuracy and the documents are not sensitive. Nothing is destructive either way: Sortio previews every move before applying and keeps backup archives of your most recent sorts, so a sort can be undone from inside the app.',
  },
  {
    question: 'How do I file new scans after the backlog is done?',
    answer:
      "Once the backlog is sorted, run the same prompt on each new batch: point Sortio at your scanner's output folder, click Analyze contents, check the preview, and apply. On Sortio Pro, watch folders can also run rules on new files as they arrive. Rules match on filenames and on the text of searchable PDFs, but they don't run OCR, so set your scanner or scanning app to save searchable PDFs (most have an OCR setting) if you want rules to read new scans. For very high volume of a single consistent document type, a rule runs deterministically without using your AI allowance at all.",
  },
];

const OrganizeScannedDocumentsBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Organize scanned documents by content', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Organize scanned documents by content',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Organize Scanned Documents by What Is Inside Them',
    description:
      'Scanned files have no useful name and no metadata, so you have to read the content. Give scans a text layer (searchable PDFs, or OCRmyPDF for image-only scans), file each by what it actually is, and build a clean per-type archive. Folder tree and a working Sortio prompt included.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Organize Scanned Documents by What Is Inside Them"
        description="Scanned files have no useful name and no metadata, so you have to read the content. Give scans a text layer, file each by what it actually is, and build a clean per-type archive. Folder tree and a working Sortio prompt included."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="organize scanned documents by content, sort scanned documents, organize scanned pdfs, ocr and file scanned documents, paperless workflow mac, file scans by content, sort scans by what is inside, name scanned documents automatically"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Organize Scanned Documents by What Is Inside Them
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>10 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <h2 className="text-2xl font-semibold text-apple-50 mt-4 mb-4">
            How to organize scanned documents by what is inside them
          </h2>

          <p className="text-lg">
            To organize scanned documents by what is inside them, you read the content, because a
            scanned file has no useful name and no metadata to sort on. Two steps: make sure every
            scan has a text layer (searchable PDFs already do; image-only scans get one in bulk from
            a free tool like OCRmyPDF), then let Sortio read that text with an LLM and file each
            document by what it actually is (a lease, a 1099, an insurance policy, a signed
            contract) rather than by a meaningless name like scan_0421.pdf. You point it at the
            folder of mixed scans, run Preview to confirm the proposed names and destinations, then
            apply.
          </p>

          <p>
            That is the whole game with scanned paper. The filename is noise, the metadata is empty
            or wrong, and the only reliable signal is the text on the page. Everything below is the
            detailed version: why scans defeat ordinary tools, why reading the content is the only
            approach that survives a real-world drawer of mixed documents, the folder structure that
            holds up, and a working prompt you can paste in today.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <ScanLine className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Read the content. A scanned file has no useful name and no metadata, so sorting by
                  filename or date is useless. Give every scan a text layer (searchable PDFs have one
                  already; OCRmyPDF adds one to image-only scans for free), then Sortio reads that
                  text with an LLM and files each scan by what the document actually is into a clean
                  per-type folder tree. One prompt handles a mixed drawer of leases, tax forms,
                  medical bills, and contracts, and it is robust to OCR noise that breaks regex-based
                  tools.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Why scanned documents are uniquely hard to sort
          </h2>

          <p>
            A typical digital file gives you something to work with. A photo has EXIF date and GPS. A
            downloaded invoice often has a sensible name and a creation date. A scanned document gives
            you almost nothing. The filename is whatever the scanner assigned, usually
            scan_0421.pdf or 20260605_0001.pdf or IMG_4471.jpg. The created date is the day you ran
            the scanner, not the day the document is from. And the file is frequently image only, so
            there is not even a text layer to search.
          </p>

          <p>
            The result is the scanned-document drawer that almost everyone has somewhere: a single
            folder, hundreds of files, every one named like its neighbor, holding a decade of leases,
            tax forms, medical bills, warranties, IDs, and signed contracts in no order at all. You
            cannot sort it by name (the names are interchangeable). You cannot sort it by date (the
            dates are all the scan date). You cannot search it (no text layer). The information that
            would let you organize it is trapped inside the pages.
          </p>

          <p>
            So the only approach that actually works is to read each page and decide what the document
            is from its content. For image-only scans that means OCR first to recover the text, then
            classification on the recovered text. This is exactly the workflow research librarians
            recommend for keeping a collection usable. As Stanford University Libraries puts it, you
            should be{' '}
            <a
              href="https://guides.library.stanford.edu/data-best-practices"
              className="text-sortio-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              consistent and descriptive in naming and organizing files
            </a>{' '}
            so that it is obvious where to find a document and what it contains. With scans you cannot
            get there by hand at any real volume, which is where content-reading automation comes in.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            OCR when there is no text layer
          </h2>

          <p>
            The first step for any scanned file is to make the words on the page readable by software.
            If the scan was saved as a searchable PDF, the OCR was already done at scan time and the
            text layer is there. If it was saved as an image-only PDF or a JPG (the default on a lot
            of phone scanning apps and older scanners), there is no text layer, and you have to OCR
            it before anything else can read it.
          </p>

          <p>
            Sortio reads the text layer that is already in the file; it does not run its own OCR. So
            for image-only scans, add the layer first. OCRmyPDF is a well-regarded free tool that
            adds a searchable text layer to existing PDFs in bulk (a single command handles a whole
            drawer), and Sortio will happily read the layer it produces. After that one-time pass,
            an image-only drawer classifies just as well as a searchable one: real text that a model
            can reason about.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Why reading the content beats matching the name
          </h2>

          <p>
            The reason content reading wins on scans is that OCR output is noisy and inconsistent, and
            an LLM is robust to that noise in a way a regex never is. The same field comes out
            differently from one scan to the next. "Date of Birth," "D.O.B.", and "DOB" all mean the
            same thing. "Acct No 0123-4567", "Account Number 0123 4567", and "A/C 01234567" are the
            same account. A model reads all of these as what they are. A regex matches one exact
            spelling and silently fails on the rest.
          </p>

          <p>
            Reading the content also lets the tool decide what a document is, not just pull a field
            out of it. A model can look at a page and conclude "this is a residential lease," "this
            is a 1099-MISC," "this is an explanation of benefits from an insurer," because it
            understands the structure and language of the document. That classification is the part
            that name-matching and field-extraction tools cannot do, and it is the part that turns a
            shapeless drawer into a filed archive.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A folder structure that holds up
          </h2>

          <p>
            File by document type at the top, then by year, with the person or entity in the filename.
            Type-first keeps related paper together so a tax pull or an insurance claim is a
            single-folder operation. Year-second keeps each folder small and makes retention easy
            (you can keep a tax year as long as you need to). This is the structure that scales from
            a hundred scans to ten thousand:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Documents/Scans/
  Tax/
    2025/
      2025-04-10_1099-NEC_Upwork.pdf
      2025-04-12_W-2_Acme_Corp.pdf
    2026/
      2026-01-31_1099-INT_Chase.pdf
  Medical/
    2026/
      2026-03-02_EOB_BlueCross.pdf
      2026-03-18_Lab_Results_Quest.pdf
  Legal/
    2026/
      2026-02-14_Lease_123_Main_St.pdf
      2026-05-01_Contract_VendorName.pdf
  Property/
    2026/
      2026-02-09_Insurance_Policy_StateFarm.pdf
      2026-04-22_Warranty_Bosch_Dishwasher.pdf
  Personal/
    2026/
      2026-01-05_Passport_JaneDoe.pdf`}
          </pre>

          <p>
            The naming convention that makes that tree searchable and self-sorting is a single
            pattern:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`{YYYY-MM-DD}_{DocType}_{Party}.pdf`}
          </pre>

          <p>
            ISO date first so files sort chronologically in any file manager without a custom view.
            Document type next so you can skim a folder and know what each file is. The party (the
            issuer, counterparty, or person the document concerns) last so two documents of the same
            type on the same day stay distinct. The date you want is the date on the document, which
            the content reading recovers, not the scan date the file system recorded.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How the tools compare on scanned documents
          </h2>

          <p>
            There are several reasonable ways to attack a drawer of scans, and they are genuinely
            different tools for different jobs. Here is the honest comparison.
          </p>

          <div className="overflow-x-auto not-prose my-6">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-apple-700/40">
                  <th className="py-3 pr-4 font-semibold text-apple-100">Approach</th>
                  <th className="py-3 pr-4 font-semibold text-apple-100">Reads content</th>
                  <th className="py-3 pr-4 font-semibold text-apple-100">Classifies by meaning</th>
                  <th className="py-3 font-semibold text-apple-100">Best for</th>
                </tr>
              </thead>
              <tbody className="text-apple-300">
                <tr className="border-b border-apple-700/20">
                  <td className="py-3 pr-4 align-top">Manual sorting</td>
                  <td className="py-3 pr-4 align-top">You do</td>
                  <td className="py-3 pr-4 align-top">You do</td>
                  <td className="py-3 align-top">Tiny piles, one-off filing</td>
                </tr>
                <tr className="border-b border-apple-700/20">
                  <td className="py-3 pr-4 align-top">Batch renamers (A Better Finder Rename, NameChanger)</td>
                  <td className="py-3 pr-4 align-top">No</td>
                  <td className="py-3 pr-4 align-top">No</td>
                  <td className="py-3 align-top">Bulk name fixes on already-known files</td>
                </tr>
                <tr className="border-b border-apple-700/20">
                  <td className="py-3 pr-4 align-top">Hazel (rules + regex on text layer)</td>
                  <td className="py-3 pr-4 align-top">Via regex match</td>
                  <td className="py-3 pr-4 align-top">Limited, per-pattern</td>
                  <td className="py-3 align-top">Clean scans from a consistent source</td>
                </tr>
                <tr className="border-b border-apple-700/20">
                  <td className="py-3 pr-4 align-top">paperless-ngx (self-hosted DMS)</td>
                  <td className="py-3 pr-4 align-top">Yes (OCR + tags)</td>
                  <td className="py-3 pr-4 align-top">Rule, pattern, and ML "Auto" based</td>
                  <td className="py-3 align-top">A searchable stored archive with a web UI</td>
                </tr>
                <tr className="border-b border-apple-700/20">
                  <td className="py-3 pr-4 align-top">Dedicated OCR tools (OCRmyPDF, Tesseract)</td>
                  <td className="py-3 pr-4 align-top">Yes (text only)</td>
                  <td className="py-3 pr-4 align-top">No</td>
                  <td className="py-3 align-top">Adding a text layer before something else files them</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 align-top text-apple-100">Sortio (AI by content)</td>
                  <td className="py-3 pr-4 align-top text-apple-100">Yes (text layer + LLM; pre-OCR image-only scans)</td>
                  <td className="py-3 pr-4 align-top text-apple-100">Yes</td>
                  <td className="py-3 align-top text-apple-100">Mixed drawers, naming and filing into plain folders</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            paperless-ngx deserves a fair word, because it is excellent at what it does. It OCRs
            incoming scans, stores them in its own database, and gives you tags and full-text search
            through a web interface. The classification leans on correspondents, document types, and
            a trainable "Auto" machine-learning matcher, and the documents live inside its archive
            rather than your normal folders.
            If you want a true document management system, that is a strength. If you want your scans
            named and dropped into ordinary folders you already use, it is more system than you need.
            Sortio sits at the lighter end: it reads each scan with an LLM, decides what it is, and
            files it into plain folders with a clean name. Dedicated OCR tools like OCRmyPDF solve the
            text-layer half of the problem and pair well with either approach.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A working Sortio prompt for a drawer of scans
          </h2>

          <p>
            Drop this into the Sortio prompt box, point it at the folder where your scans pile up, and
            run Preview before applying. It handles a mixed drawer in one pass.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each scanned document's text. Decide which
type each document is: tax, medical, legal, property,
or personal.

Tax documents (W-2, 1099, tax returns, receipts kept for
taxes) go to ~/Documents/Scans/Tax/{year}/.

Medical documents (bills, EOBs, lab results, prescriptions)
go to ~/Documents/Scans/Medical/{year}/.

Legal documents (leases, contracts, agreements, court
papers) go to ~/Documents/Scans/Legal/{year}/.

Property documents (insurance policies, warranties, deeds,
appraisals) go to ~/Documents/Scans/Property/{year}/.

Personal documents (IDs, passports, certificates) go to
~/Documents/Scans/Personal/{year}/.

Anything you cannot confidently classify goes to
~/Documents/Scans/Review/ untouched.

Rename every file to {YYYY-MM-DD}_{DocType}_{Party}.pdf,
where the date is the date printed on the document (not
the scan date), DocType is the specific form or document
name, and Party is the issuer, counterparty, or person
the document concerns. Use short canonical names.`}
          </pre>

          <p>
            Click Preview. Sortio shows the proposed name and target folder for every scan, plus the
            fields it pulled off each page. Anything it is unsure about lands in the Review folder
            untouched, so a low-confidence classification never gets silently misfiled. Untick any
            file you would rather leave in place, or refine the prompt and preview again until
            the plan looks right. Nothing moves until you apply.
          </p>

          <p>
            Apply commits the moves. Nothing is destructive: the preview-before-apply step is the
            safety net, and Sortio keeps backup archives of your most recent sorts so a sort can
            be undone from inside the app. A several-hundred-file drawer typically
            finishes in a few minutes on the managed AI tier.
          </p>

          <p>
            If you want the field-extraction detail behind the renaming (how Sortio pulls vendor,
            date, and amount cleanly out of OCR text), that is covered in the companion piece on{' '}
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="text-sortio-primary hover:underline"
            >
              automatically renaming PDFs by content
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Building a paperless workflow that stays clean
          </h2>

          <p>
            Sorting the backlog once is satisfying, but the drawer fills back up unless the new scans
            get handled too. The simplest habit: when a new batch lands, point the same prompt at the
            folder your scanner writes to, click Analyze contents, check the preview, and apply. On
            Sortio Pro ($14.99/month or $99/year), a watch folder can also run rules on new files as
            they arrive. Rules don&apos;t run OCR, so set your scanner or scanning app to save
            searchable PDFs (most have an OCR option) if you want a rule to match on a scan&apos;s text.
          </p>

          <p>
            For the first week, leave the watch in Preview mode so Sortio queues proposed moves and
            notifies you instead of applying them, which lets you confirm the classification is right
            on your particular mix of documents. After a week of clean previews, switch to Apply, and
            the paperless flow runs itself. For a very high volume of a single consistent document
            type, where the routing is deterministic, you can promote that flow to an AI Rule Builder
            rule that does not use your AI allowance at all.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Privacy and local processing
          </h2>

          <p>
            Scanned documents are often the most sensitive files a person owns: tax returns, medical
            records, IDs, signed legal agreements. Sortio supports local-only processing through
            Ollama, so the LLM runs on your own machine (Llama 3, Mistral) and no scanned content ever
            leaves it. Setup takes a few minutes. Managed AI and bring-your-own-key are also available
            when speed matters more and the documents are not sensitive. The honest trade-off between
            the two is laid out in the piece on{' '}
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="text-sortio-primary hover:underline"
            >
              local AI vs cloud AI for file organization
            </Link>
            : local is slower and slightly less accurate, but fully functional and the right pick for
            anything you would not want a third-party provider to read.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/batch-organize-documents-per-client"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Batch organize documents per client automatically
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/make-client-files-audit-ready"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Make client files audit-ready
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try this on your scan drawer</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance. Point Sortio at a folder of
              unnamed searchable scans, run Preview, and see what the classified, renamed output
              would look like before applying. No credit card required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrganizeScannedDocumentsBlogPage;
